import {
  annaVladiEditorialImages,
  meiirbekDariaEditorialImages,
  tatianaEditorialImages,
  viktorNataliaEditorialImages,
} from "./editorialAssets";

export const editorialsData = [
  {
    id: "meiirbek-nariman-daria-prague",
    templateType: "classic",
    title: "Meiirbek Nariman & Daria",
    location: "Prague, Czechia",
    content: {
      intro:
        "Some stories feel meant for a city. Meiirbek Nariman from Kazakhstan and Daria from Ukraine met in Prague, and their connection carries the same beauty as the streets around them: graceful, warm, and quietly cinematic.",
      introColumns: [
        "We started near the National Theatre, moving through the historic center while the city shifted from grand architecture to intimate details. Their energy was romantic but easy, with the kind of laughter that turns every frame into a real memory.",
        "Later we slipped into hidden courtyards, away from the crowds and noise. Their funny dog joined us and kept stealing scenes in the best way, adding spontaneity to every sequence and making the whole session feel even more alive.",
      ],
      momentTitle: "Prague Between Grand Streets and Quiet Courtyards",
      momentText:
        "One moment stayed with me: strangers stopping to photograph us as if we were part of the city itself. It was unexpected, playful, and impossible not to smile at.",
      aftermath:
        "After our time together, they continued the celebration with guests while this chapter stayed just for them. This gallery preserves that private part of the day: two people in love, a city full of history, and a rhythm that felt completely their own.",
    },
    images: {
      cover: meiirbekDariaEditorialImages.cover,
      hero: meiirbekDariaEditorialImages.hero,
      gridWide: meiirbekDariaEditorialImages.wide1,
      gridPortrait: meiirbekDariaEditorialImages.portrait,
      gridSquare: meiirbekDariaEditorialImages.square,
      momentWide: meiirbekDariaEditorialImages.wide2,
      finale: meiirbekDariaEditorialImages.finale,
      finaleObjectPosition: "center 78%",
      fullGallery: meiirbekDariaEditorialImages.fullGallery,
    },
  },
  {
    id: "anna-vladi-dnipro-sunset",
    templateType: "atmosphere",
    title: "Anna Vladi",
    location: "Dnipro River, Ukraine",
    content: {
      intro:
        "Some portrait sessions become more than a shoot. With Anna Vladi, a wedding host used to holding attention, the evening on the Dnipro river turned into a quiet revelation about softness, power, and the kind of femininity that does not ask for permission.",
      introColumns: [
        "We photographed her on the riverbank at sunset, letting the last warm light shape every movement. The atmosphere felt weightless and intimate, and each frame revealed a version of Anna that was at once elegant, sensual, and completely sincere.",
        "For her, this series became a genuine discovery. She saw herself from another side: more feminine, more passionate, more open. When the images appeared online, her Instagram filled almost instantly with messages of admiration and surprise.",
      ],
      momentTitle: "Sunset, Water, and a Different Reflection",
      momentText:
        "What stayed with me most was how quickly self-awareness turned into confidence. The closer we moved toward sunset, the more natural her presence became, until the portraits no longer felt posed at all, only fully lived.",
      aftermath:
        "At the very end of the session, I decided to introduce a note of drama. The final portrait, a close frame of Anna with wet hair, carries a different energy: deeper, bolder, and almost cinematic. It completed the story with exactly the intensity the evening had been quietly building toward.",
    },
    images: {
      cover: annaVladiEditorialImages.cover,
      hero: annaVladiEditorialImages.hero,
      gridWide: annaVladiEditorialImages.wide1,
      gridPortrait: annaVladiEditorialImages.portrait,
      gridSquare: annaVladiEditorialImages.square,
      momentWide: annaVladiEditorialImages.wide2,
      finale: annaVladiEditorialImages.finale,
      fullGallery: annaVladiEditorialImages.fullGallery,
    },
  },
  {
    id: "tatiana-prague-old-town",
    templateType: "modern-minimal",
    title: "Tatiana",
    location: "Prague Old Town, Czechia",
    content: {
      intro:
        "An afternoon spent walking through the historic center of Prague, where the conversation mattered just as much as the photographs. Tatiana has the kind of grounded presence that naturally dictates the pace. As the founder of a center for autistic children, her daily life requires immense emotional capacity. Yet here, among the centuries-old cobblestones, she carried herself with a striking, effortless lightness.",
      introColumns: [
        "Prague is a city of heavy architecture, layered with stone, deep shadows, and history. It’s easy for a subject to get lost against such a monumental backdrop. But we didn’t try to outshout the city. We let the surroundings stay simple, looking for clean geometry and quiet corners where the portraits could breathe.",
        "There was no need to direct her. Every gesture came from a place of absolute self-sufficiency. For someone whose life is dedicated to caring for others, this walk felt like a rare, well-deserved pause—a moment to simply exist, anchored and calm, in the middle of a moving city.",
      ],
      momentTitle: "Grace and Stone",
      momentText:
        "What stays with you after meeting Tatiana is her balance. She doesn’t need loud confidence to hold a frame. We kept the focus entirely on that inner steadiness—just the natural light of the old streets and a woman completely at peace with who she is.",
      aftermath:
        "These photographs aren't about performance or editorial styling. They are a visual record of a genuine dialogue. A portrait of a woman shaped by responsibility and deep empathy, taking a slow walk through a timeless city.",
    },
    images: {
      cover: tatianaEditorialImages.cover,
      hero: tatianaEditorialImages.hero,
      gridWide: tatianaEditorialImages.wide1,
      gridPortrait: tatianaEditorialImages.portrait,
      gridSquare: tatianaEditorialImages.square,
      momentWide: tatianaEditorialImages.wide2,
      finale: tatianaEditorialImages.finale,
      finaleObjectPosition: "center 65%",
      fullGallery: tatianaEditorialImages.fullGallery,
    },
  },
  {
    id: "viktor-natalia-odessa-prewedding",
    templateType: "cinematic",
    title: "Viktor & Natalia",
    location: "Odessa, Ukraine",
    content: {
      intro:
        "Before the formal vows and the cliffside ceremony, there was an afternoon on the open coast of Odessa. Viktor and Natalia met the shoreline at full speed, stripping away every expectation of how a couple is supposed to stand in front of a lens.",
      introColumns: [
        "Working along the surf leaves no room for stiff posing. The coastal wind whipped through damp linen, kicking up sea spray and scattering any thought of choreography. We moved fast—running down the tide line, turning, and catching unscripted bursts of laughter against the rush of the Black Sea.",
        "The strongest portraits always happen when the subjects forget the camera exists. There were no pauses for corrections or calculated looks, just two people charging into shallow breaks, letting cold salt water hit their skin while the afternoon sun dropped lower across the horizon.",
      ],
      momentTitle: "Saltwater and Full Speed",
      momentText:
        "The sharpest frames came out of complete unpredictability: a sudden sprint through the shallows, spray caught mid-air against hard light, and laughter that carried effortlessly over the tide. Nothing was posed; everything was driven by raw momentum.",
      aftermath:
        "A prelude to their wedding that felt closer to an untamed afternoon by the water than a scheduled session. Viktor and Natalia left the shoreline with soaked hems, salt in their hair, and an easy, kinetic energy that set the tone for everything that followed.",
    },
    images: {
      cover: viktorNataliaEditorialImages.cover,
      hero: viktorNataliaEditorialImages.hero,
      gridWide: viktorNataliaEditorialImages.wide1,
      gridPortrait: viktorNataliaEditorialImages.portrait,
      gridSquare: viktorNataliaEditorialImages.square,
      momentWide: viktorNataliaEditorialImages.wide2,
      finale: viktorNataliaEditorialImages.finale,
      fullGallery: viktorNataliaEditorialImages.fullGallery,
    },
  },
];
